import IntlMessageFormat from 'intl-messageformat';

import { langStore } from "~/stores/lang";
import type { DeepStringObj } from "~/interfaces";
import { IGetMenuRecipeIngredient, IRecipeIngredient } from "~/providers/interfaces";
import { roundNumberTo } from "./utils";
import { STAT_TYPES } from "~/const";

/**
 * Get text function. Translate for the current language and the key.
 *
 * @param  {String} key
 * @param  {Object} [replace] Replace all {} in the string
 * @return {String}
 * @memberof $i18n
 * @method trans
 */
function makeTranslate(source: DeepStringObj, key: string, lang: string, replace?: DeepStringObj): string {
	let translate = key || "";
	let curSource = source;

	if (curSource) {
		const parts = key.split(".");
		const len = parts.length;

		parts.every((item, ind) => {
			if (item in curSource) {
				curSource = curSource[item] as any;

				if (ind === len - 1) {
					translate = curSource as any;

					return false;
				}
			} else {
				return false;
			}

			// go on
			return true;
		});
	}

	return new IntlMessageFormat(translate, lang).format(replace) as string;
}

export function trans(key: string, replace?: DeepStringObj): string {
	const { data, lang } = langStore.getState();

	return makeTranslate(data, key, lang, replace);
}

// existuje preklad pro klic?
export function hasTrans(key: string): boolean {
	let curSource: string | number | DeepStringObj = langStore.getState().data;

	for (const part of key.split(".")) {
		if (!curSource || typeof curSource !== "object" || !(part in curSource)) {
			return false;
		}

		curSource = curSource[part];
	}

	return typeof curSource === "string";
}

// preklad kodu chyby ze serveru (errors.<kod>), neprevedene nebo nezname chyby jako obecna hlaska
export function transError(code: string, params?: DeepStringObj): string {
	const key = `errors.${code}`;

	if (code && hasTrans(key)) {
		try {
			return trans(key, params);
		} catch (exc) {
			// v textu je promenna, kterou server neposlal
			/* eslint-disable no-console */
			console.warn(`Chyba prekladu: ${code}`, exc);
		}
	}

	/* eslint-disable no-console */
	console.warn(`Neprelozena chyba: ${code}`);

	return trans("errors.common.unknown");
}

/* eslint-disable no-magic-numbers */
export function getPortions(portions: number): string {
	return trans("common.portions", { count: portions });
}

// pocitatelne jednotky (cesky nazev z DB units.type) -> klic s plurály
const COUNTABLE_UNITS: Record<string, string> = {
	kus: "common.amount",
	lžička: "common.amount2",
	lžíce: "common.amount3",
	hrst: "common.amount4",
	špetka: "common.amount5",
	lístek: "common.amount6",
	snítka: "common.amount7",
};

// nazev jednotky z DB (cesky units.type slouzi jako kod) -> preklad units.<kod>, bez prekladu beze zmeny (g, ml…)
export function getUnitName(unit: string): string {
	const key = `units.${unit}`;

	return hasTrans(key) ? trans(key) : unit;
}

// typ statistiky z DB (cesky stats_type.type slouzi jako kod, viz STAT_TYPES) -> preklad, neznamy beze zmeny
const STAT_TYPE_KEYS: Record<string, string> = {
	[STAT_TYPES.WEIGHT]: "stats.weight",
	[STAT_TYPES.WAIST]: "stats.waist",
	[STAT_TYPES.CHEST]: "stats.chest",
	[STAT_TYPES.SIDE]: "stats.side",
	[STAT_TYPES.ENERGY]: "stats.energy",
};

export function getStatTypeName(type: string): string {
	return type in STAT_TYPE_KEYS ? trans(STAT_TYPE_KEYS[type]) : type;
}

export function getAmount(ingredient: IRecipeIngredient | IGetMenuRecipeIngredient): string {
	const unit = "unit_name" in ingredient ? ingredient.unit_name : ingredient.unit;
	const multipleNum = typeof ingredient.multiple === "string" ? parseFloat(ingredient.multiple) : ingredient.multiple;
	const multiple = roundNumberTo(multipleNum, 2);

	if (unit in COUNTABLE_UNITS) {
		return trans(COUNTABLE_UNITS[unit], { count: multiple });
	}

	return `${multiple} ${getUnitName(unit)}`;
}
